/**
 * Haptic feedback — native-only, web-safe.
 *
 * On the Capacitor Android build (`__NATIVE__`) these call into the device's
 * vibration motor. On web they fall back to the Vibration API where available,
 * and otherwise no-op silently. Every call is fire-and-forget so callers never
 * need to await or guard.
 */

type ImpactWeight = 'light' | 'medium' | 'heavy';

// Lazily-resolved module handle so we only import the plugin once on native.
let pluginPromise: Promise<typeof import('@capacitor/haptics')> | null = null;

function loadPlugin() {
  if (!pluginPromise) pluginPromise = import('@capacitor/haptics');
  return pluginPromise;
}

function webVibrate(pattern: number | number[]) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* ignore — some browsers gate this behind a user gesture */
    }
  }
}

/** A short tap — use for selections, toggles, swipe threshold crossings. */
export function tapLight() {
  if (__NATIVE__) {
    void loadPlugin().then(({ Haptics, ImpactStyle }) =>
      Haptics.impact({ style: ImpactStyle.Light }),
    ).catch(() => {});
  } else {
    webVibrate(8);
  }
}

/** A firmer tap — use for completing a chore, confirming an action. */
export function tapMedium() {
  if (__NATIVE__) {
    void loadPlugin().then(({ Haptics, ImpactStyle }) =>
      Haptics.impact({ style: ImpactStyle.Medium }),
    ).catch(() => {});
  } else {
    webVibrate(16);
  }
}

/** A heavy thud — reserve for big moments (level/goal hit before celebration). */
export function tapHeavy() {
  if (__NATIVE__) {
    void loadPlugin().then(({ Haptics, ImpactStyle }) =>
      Haptics.impact({ style: ImpactStyle.Heavy }),
    ).catch(() => {});
  } else {
    webVibrate(28);
  }
}

/** A buzz of a precise length (ms) — for building custom rhythms like "ta-da". */
export function buzz(durationMs: number) {
  if (__NATIVE__) {
    void loadPlugin().then(({ Haptics }) => Haptics.vibrate({ duration: durationMs })).catch(() => {});
  } else {
    webVibrate(durationMs);
  }
}

/** Success notification buzz — the "you did it" celebration pattern. */
export function celebrate() {
  if (__NATIVE__) {
    void loadPlugin().then(({ Haptics, NotificationType }) =>
      Haptics.notification({ type: NotificationType.Success }),
    ).catch(() => {});
  } else {
    webVibrate([0, 18, 60, 32]);
  }
}

/** Warning buzz — for destructive confirmations or errors. */
export function warn() {
  if (__NATIVE__) {
    void loadPlugin().then(({ Haptics, NotificationType }) =>
      Haptics.notification({ type: NotificationType.Warning }),
    ).catch(() => {});
  } else {
    webVibrate([0, 24, 40, 24]);
  }
}

/** Selection tick — light feedback while dragging across selectable items. */
export function selection(weight: ImpactWeight = 'light') {
  if (weight === 'heavy') return tapHeavy();
  if (weight === 'medium') return tapMedium();
  return tapLight();
}
